import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import {ModalComponent} from '../../modal/modal.component';
import {Coords} from './coords';
import {FilesUploaderService} from '../files-uploader.service';

/**
 * Это для использования jQuery
 */
declare const $: any;

@Component({
    selector: 'app-image-cropper-modal',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './image-cropper-modal.component.html',
    styles: [
        `
            img {
                height: auto;
                max-width: 100%;
                max-height: 600px;
                margin: 0 auto;
                visibility: hidden;
            }

            /* Это нужно для выравнивания виджета */
            .jcrop-container {
                margin: 0 auto;
            }

            canvas {
                display: none;
            }
        `
    ]
})

export class ImageCropperModalComponent implements OnInit, OnDestroy {
    /**
     * Пропорции изображения
     */
    @Input() ratio: any = NaN;

    /**
     * Событие для передачи кадрированного изображения
     */
    @Output() fileCropped = new EventEmitter<Blob>();
    @Output() fileCroppedError = new EventEmitter<string>();

    @ViewChild('cropperContainer') private cropperContainer: ElementRef;
    @ViewChild('canvas') private canvasElement: ElementRef;
    @ViewChild(ModalComponent) private modal: ModalComponent;

    private canvas: HTMLCanvasElement;

    private coords = new Coords();

    private imageUrl: any;

    /**
     * Объект API планига Jcrop
     * http://deepliquid.com/content/Jcrop_API.html
     */
    private jCrop;

    private file: Blob;

    constructor(
        private cd: ChangeDetectorRef,
        private uploaderService: FilesUploaderService,
    ) {
    }

    ngOnInit() {
        this.canvas = this.canvasElement.nativeElement;
    }

    showCropper(file: Blob) {
        this.file = file;

        if (!this.file) {
            return;
        }

        this.clearCropper();
        this.prepareImageFromFile();
        this.modal.open();
    }

    /**
     * Кадрировать изображение
     */
    cropFile() {
        try {
            /**
             * Если jCrop не иниализировался, значит какая-то проблема с самим файлом
             * и достаточно бросить исключение равно как и другую возможную ошибку
             */
            if (!this.jCrop) {
                throw null;
            }

            const ctx = this.canvas.getContext('2d');

            this.canvas.width = this.coords.width;
            this.canvas.height = this.coords.height;

            /**
             * Это чтобы не было прозрачности и была возможность делать JPG
             */
            ctx.fillStyle = '#fff';
            ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

            ctx.drawImage(
                this.cropperContainer.nativeElement,
                this.coords.x,
                this.coords.y,
                this.coords.width,
                this.coords.height,
                0,
                0,
                this.canvas.width,
                this.canvas.height
            );

            /**
             * Забираем изображение из Canvas и создаем из него JPG файл
             */
            const blob = this.uploaderService.dataURItoBlob(this.canvas.toDataURL('image/jpeg'));

            this.fileCropped.emit(blob);
        } catch (e) {
            this.fileCroppedError.emit('Не удалось подготовить изображение для загрузки. Убедитесь в корректности файла изображения.');
        }

        this.modal.close();
    }

    onModalShow() {
        this.initCropper();
    }

    onModalClose() {
        this.destroyCropper();
        this.clearCropper();
        this.cd.detectChanges();
    }

    ngOnDestroy() {
        this.clearCropper();
        this.destroyCropper();
    }

    /**
     * Выделить всю область изображения
     */
    setFullSelection() {
        if (this.jCrop && this.jCrop.setSelect) {
            this.jCrop.setSelect([0, 0, 1000, 1000]);
        }
    }

    private prepareImageFromFile() {
        const imageUrl = URL.createObjectURL(this.file);
        this.imageUrl = imageUrl;
        const img: HTMLImageElement = this.cropperContainer.nativeElement;
        img.src = this.imageUrl;
    }

    private clearCropper() {
        if (this.imageUrl) {
            URL.revokeObjectURL(this.imageUrl);
        }

        this.imageUrl = null;
        const img: HTMLImageElement = this.cropperContainer.nativeElement;
        img.src = null;
        img.removeAttribute('style');
    }

    private initCropper() {
        const self = this;

        this.destroyCropper();

        $(this.cropperContainer.nativeElement).Jcrop({
            minSize: [0, 0],
            setSelect: [0, 0, 200, 200],
            aspectRatio: this.ratio,
            allowSelect: false,
            onSelect: (coords) => {
                /**
                 * При изменении выделеной области пересчитываем координаты изображения
                 */
                this.coords.setCoordsAsAbsolute(this.cropperContainer.nativeElement, coords);
            }
        }, function () {
            self.jCrop = this;
            const widgetWidth = this.getWidgetSize()[0];
            /**
             * Устанавливаем ширину для масштабирования и масштабируем координаты под инициализированные по умолчанию
             */
            self.coords.setPreviewWidth(widgetWidth);
            self.coords.setCoordsAsAbsolute(self.cropperContainer.nativeElement, this.tellSelect());
        });
    }

    private destroyCropper() {
        if (this.jCrop) {
            this.jCrop.destroy();
            this.jCrop = null;
        }
    }
}
